import { sumBy } from "~/utils/tsUtils"

export function calculateRootMeanSquareError(
  predicted: number[],
  actual: number[],
): number | undefined {
  if (predicted.length === 0 || predicted.length !== actual.length) {
    return undefined
  }

  const n = predicted.length
  let sum = 0

  for (let i = 0; i < n; ++i) {
    const diff = (predicted[i] ?? 0) - (actual[i] ?? 0)
    sum += Math.pow(diff, 2)
  }

  return Math.sqrt(sum / n)
}

export function round(num: number, digits: number = 0) {
  if (digits === 0) return Math.round(num)
  const factor = 10 ** digits
  return Math.round(num * factor) / factor
}

export function roundTo(num: number, factor: number) {
  return Math.round(num * factor) / factor
}

// `|| 0`: Math.round(-0.3) is -0, which formats as "-0".
export const units = (x: number) => Math.round(x) || 0

export const orderOfMagnitude = (n: number) => Number(n.toExponential().split("e")[1])

export function levenshtein(a: string, b: string): number {
  // Distnce between two strings
  const dp: any = []
  // there is a lot of typescript errors here. So by calling any here supresses all
  // the output is typed so it is not a problem

  for (let i = 0; i <= a.length; i++) {
    dp[i] = []
    for (let j = 0; j <= b.length; j++) {
      if (i === 0) dp[i][j] = j
      else if (j === 0) dp[i][j] = i
      else {
        dp[i][j] = Math.min(
          dp[i - 1][j] + 1, // deletion
          dp[i][j - 1] + 1, // insertion
          dp[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1), // substitution
        )
      }
    }
  }

  return dp[a.length][b.length] / Math.max(a.length, b.length)
}

// Distribute `newTotal` across `weights` as integers that sum exactly to round(newTotal), using the
// largest-remainder (Hamilton) method. A negative weight counts as 0, so a non-negative total never
// gives a negative share; with no positive weight the total splits evenly.
export function distributeIntegerByWeights(weights: number[], newTotal: number): number[] {
  if (weights.length === 0) return []
  const target = Math.round(newTotal)
  const clamped = weights.map((w) => Math.max(0, w))
  const weightSum = clamped.reduce((s, w) => s + w, 0)
  const base = weightSum > 0 ? clamped : clamped.map(() => 1)
  const baseSum = weightSum > 0 ? weightSum : base.length
  const exact = base.map((w) => (target * w) / baseSum)
  const floored = exact.map((v) => Math.floor(v))
  const remainder = Math.max(0, target - floored.reduce((s, v) => s + v, 0))
  const order = exact
    .map((v, i) => ({ i, frac: v - Math.floor(v) }))
    .sort((a, b) => b.frac - a.frac)
  const out = [...floored]
  for (let k = 0; k < remainder; k += 1) {
    const entry = order[k % order.length]
    if (entry) out[entry.i] = (out[entry.i] ?? 0) + 1
  }
  return out
}

/**
 * Each part rounded half away from zero, then what they miss the rounded total by goes a unit at a
 * time to the parts the rounding moved most the other way. A part that rounds to 0 takes a unit only
 * when every part does, so rounding never makes a part of its own.
 */
export function roundToTotal(values: number[], total: number): number[] {
  // `+ 0` reads -0 as 0.
  const out = values.map((v) => Math.sign(v) * Math.round(Math.abs(v)) + 0)
  let remainder = Math.round(total) - out.reduce((s, v) => s + v, 0)
  if (!Number.isFinite(remainder) || remainder === 0) return out
  const step = Math.sign(remainder)
  const nonZero = out.flatMap((u, i) => (u === 0 ? [] : [i]))
  const pool = nonZero.length > 0 ? nonZero : values.map((_, i) => i)
  // Ties go to the first part when adding and to the last when taking away.
  const order = (step > 0 ? pool : pool.reverse()).sort(
    (a, b) => step * (values[b]! - out[b]! - (values[a]! - out[a]!)),
  )
  for (let k = 0; remainder !== 0 && order.length > 0; k += 1) {
    const i = order[k % order.length]!
    out[i] = out[i]! + step
    remainder -= step
  }
  return out
}

export function distributeWithMinimum(weights: number[], minPercent: number): number[] {
  if (weights.length === 0) return []
  if (minPercent * weights.length >= 100) return weights.map(() => 100 / weights.length)

  const total = weights.reduce((sum, w) => sum + Math.max(0, w), 0)
  const raw =
    total > 0
      ? weights.map((w) => (Math.max(0, w) / total) * 100)
      : weights.map(() => 100 / weights.length)

  const lifted = raw.map((p) => Math.max(p, minPercent))
  const excess = lifted.reduce((sum, p) => sum + p, 0) - 100
  if (excess <= 0) return lifted

  const room = lifted.reduce((sum, p) => sum + (p - minPercent), 0)
  return lifted.map((p) => p - ((p - minPercent) / room) * excess)
}

export function calculateWeightedAverage<T extends Record<string, any>>(
  objects: Array<T>,
  valueKey: Extract<keyof T, string>,
  weightKey: Extract<keyof T, string>,
) {
  objects = objects.filter((obj) => typeof obj[valueKey] === "number")
  let totalWeightedValue = 0
  let totalWeight = 0
  if (objects.length === 0) {
    return undefined
  }

  for (const obj of objects) {
    const value = obj[valueKey]
    const weight = obj[weightKey] ?? 0

    totalWeightedValue += value * weight
    totalWeight += weight
  }

  return totalWeight === 0
    ? sumBy(objects, valueKey) / objects.length
    : totalWeightedValue / totalWeight
}
